import { Button, Card, Textarea, useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { NotebookPen } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { ConnectorResponse } from "@/api";
import { errorMessage, getConnectorDetailQueryKey, updateConnectorMutation } from "@/api";
import { Text } from "@/components/Text";

/** The owner's setup notes (MHS-875): everyone who can see the connector reads them; editors change them. */
export function SetupNotesCard({ connector, canEdit }: Readonly<{ connector: ConnectorResponse; canEdit: boolean }>) {
  const { t } = useTranslation("connectors");
  const toast = useToast();
  const queryClient = useQueryClient();
  const id = useId();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(connector.setupNotes ?? "");
  const update = useMutation(updateConnectorMutation());
  const notes = connector.setupNotes?.trim();
  if (!notes && !canEdit && !connector.helpSlug) return null;

  const save = () =>
    update.mutate(
      { path: { connector_uuid: connector.uuid }, body: { setupNotes: value } },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: getConnectorDetailQueryKey({ path: { connector_uuid: connector.uuid } }),
          });
          toast.success(t("template.notesSaved"));
          setEditing(false);
        },
        onError: (error) => toast.error(errorMessage(error, t("template.failed"))),
      },
    );

  return (
    <Card>
      <section aria-labelledby={`${id}-title`} className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <NotebookPen size={16} aria-hidden className="text-content-tertiary" />
          <Text as="h2" id={`${id}-title`} variant="body" weight="semibold" className="flex-1">
            {t("template.notes")}
          </Text>
          {connector.helpSlug && (
            <Link
              to={`/help/${connector.helpSlug}`}
              className="btn-focus-ring rounded text-content-primary underline underline-offset-2"
            >
              <Text as="span" variant="body-sm">
                {t("template.guideLink")}
              </Text>
            </Link>
          )}
        </div>
        {editing ? (
          <>
            <Text variant="caption" tone="tertiary">
              {t("template.notesHint")}
            </Text>
            <Textarea
              id={`${id}-notes`}
              aria-label={t("template.notes")}
              rows={4}
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={() => setEditing(false)} disabled={update.isPending}>
                {t("template.notesCancel")}
              </Button>
              <Button variant="primary" size="sm" loading={update.isPending} onClick={save}>
                {t("template.notesSave")}
              </Button>
            </div>
          </>
        ) : (
          <>
            <Text variant="body-sm" tone={notes ? "primary" : "tertiary"} className="whitespace-pre-wrap">
              {notes || t("template.notesEmpty")}
            </Text>
            {canEdit && (
              <div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setValue(connector.setupNotes ?? "");
                    setEditing(true);
                  }}
                >
                  {t("template.notesEdit")}
                </Button>
              </div>
            )}
          </>
        )}
      </section>
    </Card>
  );
}
